/**
 * The add-a-child draft — the "unsaved-state handling" half of AD-04 (#576 o6).
 *
 * A parent part-way through typing their child's name and birthday has done work. Until this
 * change step 1 held nothing at all, so a remount, a step change or a refresh lost it silently.
 *
 * This is deliberately the SAME shape as `signupDraft.js`, and not a generalisation of it. The two
 * differ where it matters — a different key, a different allowlist, a different clearing rule — and
 * one module serving both would have to carry every difference as a parameter. The rules that make
 * either safe are the ones repeated here on purpose:
 *
 *   1. ONLY THE KNOWN FIELDS ARE STORED, on the way in AND on the way out, so a future field cannot
 *      arrive in storage by accident and bytes from an older build cannot arrive in the form.
 *   2. EVERY FIELD IS TYPE-CHECKED, not just name-checked. The allowlist alone let syntactically
 *      valid rubbish through: `{"name": 7}` survived it, and the caller's `(draft.name || '').trim()`
 *      then threw a `TypeError` OUTSIDE its own try block — reproduced live, 2026-09-16, and found
 *      by the cross-model review of the first version of this file. A module that promises corrupt
 *      bytes cannot reach the form has to be the thing that checks the type.
 *   3. EVERY ENTRY POINT IS TOTAL. Storage can be denied (private mode) and stored bytes can be
 *      corrupt. Losing a draft is a small disappointment; throwing into a render is a lost family.
 *   4. THE DRAFT IS SCOPED TO THE FAMILY THAT WROTE IT. The key carries the tenant id, because
 *      `sessionStorage` outlives a sign-out within the same tab and this app clears none of it on
 *      the way out. Without the scope, the next family to sign in on that tab would be handed the
 *      previous family's child's name and birthday.
 *
 * `sessionStorage`, not `localStorage`: a child's name and birthday are personal data and must not
 * outlive the browser session.
 */

/** @type {string} The key prefix. The tenant id is appended, so one family cannot read another's. */
export const CHILD_DRAFT_KEY_PREFIX = 'chorestory.onboarding.child';

/**
 * The storage key for one family.
 *
 * @param {string} tenantId the family's own id
 * @returns {string|null} the key, or `null` when there is no usable tenant id — which every entry
 *                        point below treats as "no storage", rather than writing to a shared key
 */
export function childDraftKey(tenantId) {
  if (typeof tenantId !== 'string' || tenantId.trim() === '') return null;
  return `${CHILD_DRAFT_KEY_PREFIX}.${tenantId}`;
}

/** The only fields that may be persisted, and the type each one must be. */
const STRING_FIELDS = ['name', 'username', 'birthDate'];

/** An empty draft, which is also what a denied, absent or corrupt read returns. */
export const EMPTY_CHILD_DRAFT = { name: '', username: '', birthDate: '', usernameEdited: false };

/**
 * Keep the allowlisted fields, and keep each one ONLY when it carries the right type.
 *
 * A wrong-typed field is dropped rather than coerced. `String(7)` would put "7" in the name box as
 * though the family had typed it; an empty box is honest about having lost something.
 */
function pick(source) {
  const out = {};
  if (!source || typeof source !== 'object') return out;
  for (const field of STRING_FIELDS) {
    if (typeof source[field] === 'string') out[field] = source[field];
  }
  if (typeof source.usernameEdited === 'boolean') out.usernameEdited = source.usernameEdited;
  return out;
}

/** Whether a draft carries anything the family would miss. `usernameEdited` alone is not content. */
function isEmptyDraft(draft) {
  return !STRING_FIELDS.some((field) => typeof draft[field] === 'string' && draft[field].trim() !== '');
}

/**
 * Store the draft for one family. A draft with nothing typed in it clears the key instead of
 * writing an empty object, so a parent who never touched the form leaves nothing behind at all.
 *
 * @param {string} tenantId the family's own id; a missing one stores nothing
 * @param {Object} draft the whole form state; anything outside the allowlist is dropped
 */
export function writeChildDraft(tenantId, draft) {
  const key = childDraftKey(tenantId);
  if (key === null) return;
  const kept = pick(draft);
  if (isEmptyDraft(kept)) {
    clearChildDraft(tenantId);
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(kept));
  } catch {
    /* Storage denied. The form still works; only the draft is lost. */
  }
}

/**
 * Read one family's draft back, merged onto the empty shape so every field is a defined value of
 * the right type.
 *
 * @param {string} tenantId the family's own id; a missing one reads nothing
 * @returns {Object} `{ name, username, birthDate, usernameEdited }`. Never throws.
 */
export function readChildDraft(tenantId) {
  const key = childDraftKey(tenantId);
  if (key === null) return { ...EMPTY_CHILD_DRAFT };
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return { ...EMPTY_CHILD_DRAFT };
    return { ...EMPTY_CHILD_DRAFT, ...pick(JSON.parse(raw)) };
  } catch {
    return { ...EMPTY_CHILD_DRAFT };
  }
}

/** Forget one family's draft. Called once the child is actually on the account, and never before. */
export function clearChildDraft(tenantId) {
  const key = childDraftKey(tenantId);
  if (key === null) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* Storage denied. Nothing was stored either, so nothing is left behind. */
  }
}
